import { useState, useEffect } from "react";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import { Modal } from "@/components/ui/modal";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import { rawMaterialService } from "@/services/masterDataService";
import { handleApiError } from "@/services/api";
import type { RawMaterial } from "@/types/models";
import { useIsOwner } from "@/hooks/useRole";


export default function RawMaterialsPage() {
  const isOwner = useIsOwner();
  const [materials, setMaterials] = useState<RawMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<RawMaterial | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Form state
  const [name, setName] = useState("");
  const [unit, setUnit] = useState("kg");
  const [minStockLevel, setMinStockLevel] = useState("");
  // No screen used to set this, so every material — a premix or a medicine
  // bought dear — was costed as feed (live walk 2, W2-1). True is the
  // server's default, so a new material starts ticked.
  const [isFeedIngredient, setIsFeedIngredient] = useState(true);
  // QC standards for the receipt check. Blank means the material has no
  // standard — a measurement then has nothing to be judged against, and the
  // purchase form refuses rather than passing the load.
  const [moistureStd, setMoistureStd] = useState("");
  const [aflatoxinLimit, setAflatoxinLimit] = useState("");

  const loadMaterials = async () => {
    try {
      const data = await rawMaterialService.list();
      setMaterials(data);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMaterials();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setUnit("kg");
    setMinStockLevel("");
    setIsFeedIngredient(true);
    setMoistureStd("");
    setAflatoxinLimit("");
    setError("");
    setModalOpen(true);
  };

  const openEdit = (rm: RawMaterial) => {
    setEditing(rm);
    setName(rm.name);
    setUnit(rm.unit);
    setMinStockLevel(rm.min_stock_level?.toString() ?? "");
    setIsFeedIngredient(rm.is_feed_ingredient ?? true);
    setMoistureStd(rm.moisture_std_pct?.toString() ?? "");
    setAflatoxinLimit(rm.aflatoxin_limit?.toString() ?? "");
    setError("");
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    const payload: Partial<RawMaterial> = {
      name,
      unit,
      min_stock_level: minStockLevel ? parseFloat(minStockLevel) : undefined,
      is_feed_ingredient: isFeedIngredient,
      // A blank box clears the standard rather than sending 0 — "no standard"
      // and "a standard of zero" mean opposite things to the receipt check.
      moisture_std_pct: moistureStd.trim() === "" ? undefined : parseFloat(moistureStd),
      aflatoxin_limit: aflatoxinLimit.trim() === "" ? undefined : parseFloat(aflatoxinLimit),
    };

    try {
      if (editing) {
        await rawMaterialService.update(editing.id, payload);
      } else {
        await rawMaterialService.create(payload);
      }
      setModalOpen(false);
      await loadMaterials();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageMeta
        title="Raw Materials | Poultry Book"
        description="Manage raw materials"
      />
      <PageBreadcrumb pageTitle="Raw Materials" />

      <div className="card">
        <div className="flex items-center justify-between px-5 py-4 sm:px-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            All Raw Materials
          </h3>
          {isOwner && (
            <Button size="sm" onClick={openCreate}>
              Add Material
            </Button>
          )}
        </div>

        {error && !modalOpen && (
          <div className="mx-5 mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500 sm:mx-6">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          {loading ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              Loading...
            </div>
          ) : materials.length === 0 ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              No raw materials found. Add your first material.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-t border-gray-200 dark:border-gray-800">
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Name
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Unit
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                    Min Stock Level
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    In feed cost
                  </TableCell>
                  {isOwner && (
                    <TableCell isHeader className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {materials.map((rm) => (
                  <TableRow
                    key={rm.id}
                    className="border-t border-gray-100 dark:border-gray-800"
                  >
                    <TableCell className="px-5 py-3 text-sm font-medium text-gray-800 dark:text-white/90">
                      {rm.name}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {rm.unit}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-right text-gray-600 dark:text-gray-400">
                      {rm.min_stock_level != null
                        ? `${rm.min_stock_level.toLocaleString(undefined, { maximumFractionDigits: 3 })} ${rm.unit}`
                        : "-"}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {rm.is_feed_ingredient === false ? "No" : "Yes"}
                    </TableCell>
                    {isOwner && (
                      <TableCell className="px-5 py-3 text-right">
                        <button
                          onClick={() => openEdit(rm)}
                          className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                        >
                          Edit
                        </button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        className="max-w-md p-5 sm:p-8 mx-4"
      >
        <h4 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90">
          {editing ? "Edit Raw Material" : "Add Raw Material"}
        </h4>

        {error && modalOpen && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="rmName">Name *</Label>
            <Input
              id="rmName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Maize, Soybean DOC"
              required
              maxLength={100}
            />
          </div>

          <div>
            <Label htmlFor="rmUnit">Unit *</Label>
            <select
              id="rmUnit"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            >
              <option value="kg">kg</option>
              <option value="ton">ton</option>
              <option value="g">g</option>
              <option value="litre">litre</option>
              <option value="ml">ml</option>
              <option value="unit">unit</option>
            </select>
          </div>

          <div>
            <Label htmlFor="minStock">Min Stock Level {unit && <span className="text-xs text-gray-400">({unit})</span>}</Label>
            <Input
              id="minStock"
              type="number"
              value={minStockLevel}
              onChange={(e) => setMinStockLevel(e.target.value)}
              placeholder="e.g. 500"
              min={0}
              step="any"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-400 cursor-pointer">
              <input
                id="rmFeedIngredient"
                type="checkbox"
                checked={isFeedIngredient}
                onChange={(e) => setIsFeedIngredient(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
              />
              Counts toward feed cost
            </label>
            <p className="mt-1 text-xs text-gray-400">
              Untick for medicines, vaccines, freight or anything else that is not a feed
              ingredient. It is still taken out of stock when feed is milled, but left out of the
              batch&rsquo;s cost. Batches already milled keep their cost; Edit &rarr; Save a
              batch to re-cost it.
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Receipt check standards (optional)
            </p>
            <p className="mt-0.5 mb-3 text-xs text-gray-500 dark:text-gray-400">
              What an inward load is judged against. Leave blank if this material has no standard —
              a blank is not a zero, and the purchase form will refuse a reading it cannot judge
              rather than passing the load.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="moistureStd">Standard moisture (%)</Label>
                <Input
                  id="moistureStd"
                  type="number"
                  value={moistureStd}
                  onChange={(e) => setMoistureStd(e.target.value)}
                  placeholder="e.g. 12"
                  min={0}
                  max={100}
                  step="any"
                />
                <p className="mt-1 text-xs text-gray-400">
                  Moisture above this is deducted percent for percent.
                </p>
              </div>
              <div>
                <Label htmlFor="aflaLimit">Aflatoxin limit</Label>
                <Input
                  id="aflaLimit"
                  type="number"
                  value={aflatoxinLimit}
                  onChange={(e) => setAflatoxinLimit(e.target.value)}
                  placeholder="e.g. 20"
                  min={0}
                  step="any"
                />
                <p className="mt-1 text-xs text-gray-400">
                  Above this the load is flagged — it is never deducted.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={saving || !name}>
              {saving ? "Saving..." : editing ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
